import React, { useCallback, useEffect, useRef, useState } from 'react';
import { t } from '../i18n';
import { salesAPI } from '../api';

// ─────────────────────────────────────────────────────────────────────────────
// Табло в офисе: телевизор на стене.
//
// Показывает ровно то же, что и телефон клиента, только сразу по всем заказам.
// Ни фамилий, ни телефонов, ни сумм: рядом с экраном ходят посторонние, и
// «Иванов, 90 листов» читать им незачем. Заказ назван номером — по нему его
// узнаёт и менеджер, и тот, кому этот номер дали.
//
// Логина у экрана нет намеренно. Учётка, оставленная на телевизоре без
// присмотра, рано или поздно окажется чужой, а срок токена кончится ночью — и
// утром вместо табло будет окно входа. Экран открывают один раз ссылкой с
// ключом: /tablo?k=<ключ>. Ключи заводит администратор, отзыв — удаление ключа.
//
// Размеры в vw и clamp: одну и ту же страницу открывают и на 24" мониторе
// менеджера, и на 65" телевизоре в четыре метра от стола.
// ─────────────────────────────────────────────────────────────────────────────

const POLL_MS = 15000;

// Сколько держать одну «страницу» колонки, когда заказы не влезли на экран.
// Семь секунд — время, за которое человек в другом конце комнаты успевает
// найти глазами свой номер и не начинает нервничать, что список уехал.
const PAGE_MS = 7000;

// «Готово» — за сегодняшние рабочие сутки, и это написано прямо в заголовке.
// Из «готов» заказ сам не уходит: он ждёт отгрузки, а отгрузку оформляют
// далеко не всегда — таких заказов на заводе уже три сотни. Показать их все
// значит превратить колонку в кладбище, где сегодняшнего заказа не найти.
// А смотрит табло тот, кто сидит в офисе и ждёт свой заказ: ему важно
// увидеть, как его номер переходит из «изготавливается» в «готово».
const COLUMNS = [
  { key: 'queue',      title: 'В очереди',       stages: ['accepted', 'queue'], color: 'var(--warning)' },
  { key: 'production', title: 'Изготавливается', stages: ['production'],        color: 'var(--accent)'  },
  { key: 'ready',      title: 'Готово сегодня',  stages: ['ready'],             color: 'var(--success)' },
];

// Что писать в правом нижнем углу карточки. У каждой колонки свой ответ на
// вопрос ждущего человека: в очереди — «когда дойдёт», в работе — «сколько
// осталось», в готовых — «во сколько сделали».
function cardNote(order, col) {
  if (col === 'production') {
    const left = Math.max(0, (order.quantity || 0) - (order.done || 0));
    return `осталось ${left} из ${order.quantity}`;
  }
  if (col === 'ready') {
    return `готово в ${new Date(order.updated_at).toLocaleTimeString('ru-RU',
      { hour: '2-digit', minute: '2-digit' })}`;
  }
  return `${order.progress}%`;
}

function OrderCard({ order, color, col, fresh }) {
  const done = order.progress >= 100;
  return (
    <div style={{
      background: 'var(--card)', border: '1px solid var(--border)',
      borderLeft: `4px solid ${color}`, borderRadius: 10,
      padding: 'clamp(10px, 0.9vw, 18px)', marginBottom: 'clamp(8px, 0.6vw, 14px)',
      // Заказ, который только что стал готовым, полторы минуты подсвечен.
      // Человек в офисе не смотрит на экран не отрываясь — он поднимает
      // глаза раз в пару минут, и момент нужно успеть показать.
      boxShadow: fresh ? `0 0 0 3px ${color}` : 'none',
      transition: 'box-shadow .4s ease',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'baseline', gap: 10 }}>
        <span style={{ color: 'var(--text)', fontWeight: 700,
                       fontSize: 'clamp(17px, 1.5vw, 34px)', letterSpacing: 0.5 }}>
          {order.order_number}
        </span>
        <span style={{ color: 'var(--muted)', fontSize: 'clamp(12px, 1vw, 22px)',
                       whiteSpace: 'nowrap' }}>
          {order.quantity} шт
        </span>
      </div>

      <div style={{ background: 'var(--input)', borderRadius: 4,
                    height: 'clamp(5px, 0.45vw, 10px)', marginTop: 'clamp(7px, 0.6vw, 14px)' }}>
        <div style={{
          width: `${order.progress}%`, height: '100%', borderRadius: 4,
          background: done ? 'var(--success)' : color, transition: 'width .6s ease',
        }} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between',
                    marginTop: 'clamp(5px, 0.4vw, 10px)',
                    fontSize: 'clamp(11px, 0.85vw, 19px)' }}>
        <span style={{ color: 'var(--dim)' }}>
          {order.deadline
            ? `до ${new Date(order.deadline).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}`
            : ''}
        </span>
        <span style={{ color: done ? 'var(--success)' : 'var(--muted)', fontWeight: 700 }}>
          {cardNote(order, col)}
        </span>
      </div>
    </div>
  );
}

// Колонка табло. Пока заказы влезают в высоту экрана — обычный список.
// Как только не влезают — колонка сама листает себя страницами: полоса
// прокрутки на телевизоре бесполезна, к нему никто не подходит с мышью, а
// заказ, оказавшийся ниже края, для ждущего человека просто исчезает.
//
// Листаем именно страницами, а не бегущей строкой: карточку надо успеть
// прочитать, а не поймать. Каждая колонка листает себя отдельно и в своём
// ритме — у них разное число страниц, и синхронизировать их незачем.
function TabloColumn({ col, list, total: totalRaw, fresh }) {
  const total = totalRaw ?? list.length;
  const viewRef  = useRef(null);
  const innerRef = useRef(null);
  const [geom, setGeom] = useState({ pages: 1, viewH: 0, maxShift: 0 });
  const [page, setPage] = useState(0);

  // Высоту меряем, а не считаем: карточка тянется от clamp() и от длины
  // номера заказа, и «сколько их влезет» на 24" мониторе и на 65" телевизоре
  // — разные числа. ResizeObserver ловит и смену списка, и поворот экрана.
  useEffect(() => {
    const view = viewRef.current, inner = innerRef.current;
    if (!view || !inner) return undefined;
    const measure = () => {
      const viewH = view.clientHeight;
      const contentH = inner.scrollHeight;
      setGeom({
        viewH,
        maxShift: Math.max(0, contentH - viewH),
        pages: viewH > 0 ? Math.max(1, Math.ceil(contentH / viewH)) : 1,
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(view);
    ro.observe(inner);
    return () => ro.disconnect();
  }, [list.length]);

  useEffect(() => {
    if (geom.pages <= 1) { setPage(0); return undefined; }
    const id = setInterval(() => setPage(p => (p + 1) % geom.pages), PAGE_MS);
    return () => clearInterval(id);
  }, [geom.pages]);

  // Заказы уехали (отгрузили, закрыли смену) — страниц стало меньше, чем
  // номер текущей. Пересчёт до следующего тика таймера, иначе колонка на
  // семь секунд показала бы пустоту.
  const current = Math.min(page, geom.pages - 1);
  // Последнюю страницу прижимаем к низу: иначе она была бы наполовину
  // пустой, а верхняя карточка на ней — обрезанной пополам.
  const shift = Math.min(current * geom.viewH, geom.maxShift);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 10,
        paddingBottom: 'clamp(7px, 0.6vw, 14px)',
        borderBottom: `2px solid ${col.color}`,
        marginBottom: 'clamp(9px, 0.8vw, 18px)',
      }}>
        <span style={{ color: col.color, fontWeight: 700,
                       fontSize: 'clamp(15px, 1.3vw, 30px)' }}>
          {t(col.title)}
        </span>

        {/* Точки — чтобы смотрящий понимал: список не «дёргается сам по
            себе», а листается, и его номер вернётся через круг. */}
        {geom.pages > 1 && (
          <span style={{ display: 'flex', gap: 'clamp(4px, 0.3vw, 8px)',
                         marginLeft: 'auto', alignSelf: 'center' }}>
            {Array.from({ length: geom.pages }, (_, i) => (
              <span key={i} style={{
                width: 'clamp(6px, 0.5vw, 11px)', height: 'clamp(6px, 0.5vw, 11px)',
                borderRadius: '50%',
                background: i === current ? col.color : 'var(--border)',
                transition: 'background .3s ease',
              }} />
            ))}
          </span>
        )}

        {/* В шапке — сколько заказов в колонке НА САМОМ ДЕЛЕ. Готовые
            копятся, пока их не увезут, и на экран попадают не все:
            честнее показать «24 из 310», чем молча оборвать список. */}
        <span style={{ color: 'var(--dim)', fontSize: 'clamp(13px, 1.1vw, 26px)',
                       marginLeft: geom.pages > 1 ? 'clamp(8px, 0.7vw, 16px)' : 'auto',
                       fontWeight: 700 }}>
          {total > list.length ? `${list.length} из ${total}` : list.length}
        </span>
      </div>

      <div ref={viewRef} style={{ overflow: 'hidden', flex: 1, minHeight: 0 }}>
        <div ref={innerRef} style={{
          transform: `translateY(-${shift}px)`,
          transition: 'transform .8s ease',
        }}>
          {list.map(o => (
            <OrderCard key={o.order_number} order={o} color={col.color}
                       col={col.key} fresh={!!fresh[o.order_number]} />
          ))}
          {list.length === 0 && (
            <div style={{ color: 'var(--dim)', fontSize: 'clamp(12px, 1vw, 22px)',
                          padding: '10px 2px' }}>
              {t('пусто')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TabloPage() {
  // Ключ живёт в адресе, а не в localStorage: телевизор настраивают один раз,
  // открыв ссылку, и потом к нему никто не подходит с клавиатурой.
  const token = new URLSearchParams(window.location.search).get('k') || '';
  const [data, setData]       = useState(null);
  const [error, setError]     = useState(null);
  const [clock, setClock]     = useState(new Date());
  const [updated, setUpdated] = useState('');

  // Показывал ли экран данные хоть раз. Ref, а не состояние: значение читает
  // таймер, заведённый один раз на весь день.
  const shown = useRef(false);
  // Какие заказы уже были готовы на прошлом опросе. При ПЕРВОЙ загрузке
  // просто запоминаем их: иначе включённый утром телевизор подсветил бы всю
  // колонку разом, и подсветка перестала бы что-либо значить.
  const wasReady = useRef(null);
  const [fresh, setFresh] = useState({});

  // Подсветка живёт полторы минуты: успеть заметить, но не превратить экран
  // в новогоднюю гирлянду к концу смены.
  const markFresh = useCallback((orders) => {
    const now = Date.now();
    const ready = new Set(orders.filter(o => o.stage === 'ready')
                                .map(o => o.order_number));
    const before = wasReady.current;
    wasReady.current = ready;
    setFresh(prev => {
      const next = {};
      for (const [num, ts] of Object.entries(prev)) {
        if (now - ts < 90000) next[num] = ts;
      }
      if (before) {
        for (const num of ready) if (!before.has(num)) next[num] = now;
      }
      return next;
    });
  }, []);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const r = await salesAPI.getTablo(token);
      shown.current = true;
      markFresh(r.data.orders || []);
      setData(r.data);
      setError(null);
      setUpdated(new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }));
    } catch (e) {
      // 404 — ключ отозвали, это навсегда и об этом надо сказать. Всё
      // остальное — оборвалась сеть; экран висит сутками, и гасить его из-за
      // одной неудачной попытки нельзя: старые цифры честнее пустоты, а время
      // последнего обновления в шапке покажет, что они несвежие.
      if (e?.response?.status === 404) setError('Экран не найден — ключ отозван');
      else if (!shown.current) setError('Нет связи с сервером');
    }
  }, [token, markFresh]);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    const id = setInterval(() => setClock(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  const orders  = data?.orders || [];
  const shipped = orders.filter(o => o.stage === 'shipped');

  if (!token) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ maxWidth: 520, textAlign: 'center', color: 'var(--muted)',
                      lineHeight: 1.6 }}>
          <div style={{ fontSize: 34, marginBottom: 10 }}>📺</div>
          <div style={{ color: 'var(--text)', fontSize: 19, fontWeight: 700, marginBottom: 8 }}>
            {t('Табло заказов')}
          </div>
          Адрес открыт без ключа. Ссылку для этого экрана выдаёт администратор:
          «Настройка → Настройки системы → Табло в офисе».
        </div>
      </div>
    );
  }

  return (
    // Ровно экран и ни пикселем больше. К телевизору не подходят с мышью, и
    // всё, что уехало за нижний край, для зрителя не существует — так с экрана
    // пропадал QR внизу. Список больше не растягивает страницу: лишние заказы
    // листает сама колонка.
    <div style={{
      height: '100vh', overflow: 'hidden', boxSizing: 'border-box',
      background: 'var(--bg)',
      padding: 'clamp(14px, 1.2vw, 30px)', display: 'flex', flexDirection: 'column',
    }}>
      {/* Шапка */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0,
                    marginBottom: 'clamp(12px, 1vw, 24px)' }}>
        <div style={{ color: 'var(--text)', fontWeight: 700,
                      fontSize: 'clamp(20px, 1.8vw, 40px)' }}>
          🏭 LamTech — {t('заказы в работе')}
        </div>
        {data?.branch && (
          <div style={{ color: 'var(--muted)', fontSize: 'clamp(13px, 1.1vw, 24px)' }}>
            {data.branch}
          </div>
        )}
        {/* Человек, который ждёт в офисе, наводит телефон и уходит следить за
            своим заказом сам — вместо того чтобы стоять перед телевизором и
            переспрашивать менеджера. Экран и телефон показывают одно и то же.

            Место — в шапке, рядом с названием. Внизу QR занимал целую полосу
            под собой, а полоса на телевизоре стоит дорого: это две карточки
            заказов, которых из-за неё не видно. */}
        {data?.track_qr && (
          <div style={{ display: 'flex', alignItems: 'center',
                        gap: 'clamp(7px, 0.6vw, 14px)',
                        marginLeft: 'auto', flexShrink: 0 }}>
            {/* Белая подложка обязательна: на тёмном фоне сканер QR берёт через раз */}
            <div style={{ background: '#fff', padding: 'clamp(3px, 0.25vw, 7px)',
                          borderRadius: 7, lineHeight: 0 }}>
              <img src={data.track_qr} alt="QR"
                   style={{ width: 'clamp(46px, 3.6vw, 88px)',
                            height: 'clamp(46px, 3.6vw, 88px)', display: 'block' }} />
            </div>
            <div style={{ lineHeight: 1.3 }}>
              <div style={{ color: 'var(--text)', fontWeight: 700,
                            fontSize: 'clamp(11px, 0.9vw, 20px)' }}>
                {t('Следите за заказом со своего телефона')}
              </div>
              <div style={{ color: 'var(--dim)', fontSize: 'clamp(9px, 0.75vw, 16px)',
                            marginTop: 2 }}>
                Наведите камеру и введите код заказа
              </div>
            </div>
          </div>
        )}

        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <div style={{ color: 'var(--text)', fontWeight: 700,
                        fontSize: 'clamp(20px, 1.8vw, 40px)', lineHeight: 1 }}>
            {clock.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
          </div>
          <div style={{ color: 'var(--dim)', fontSize: 'clamp(10px, 0.8vw, 17px)', marginTop: 4 }}>
            {error ? error : `обновлено ${updated}`}
          </div>
        </div>
      </div>

      {/* Колонки */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 'clamp(10px, 0.9vw, 22px)', flex: 1, minHeight: 0 }}>
        {COLUMNS.map(col => (
          <TabloColumn
            key={col.key}
            col={col}
            list={orders.filter(o => col.stages.includes(o.stage))}
            total={data?.totals?.[col.key]}
            fresh={fresh}
          />
        ))}
      </div>

      {/* Заказ, который только что увезли, не должен пропадать с экрана
          молча: менеджер отошёл на десять минут и не понимает, отгрузили
          его или он потерялся. Полосы под это не держим: пока отгрузок нет,
          нижний край экрана принадлежит заказам. */}
      {shipped.length > 0 && (
        <div style={{ marginTop: 'clamp(10px, 0.8vw, 20px)',
                      paddingTop: 'clamp(8px, 0.7vw, 16px)',
                      borderTop: '1px solid var(--border)', flexShrink: 0,
                      display: 'flex', alignItems: 'center', flexWrap: 'wrap',
                      gap: 'clamp(6px, 0.5vw, 12px)' }}>
          <span style={{ color: 'var(--dim)', fontSize: 'clamp(11px, 0.9vw, 20px)',
                         fontWeight: 700 }}>
            🚚 {t('ОТГРУЖЕНО')}:
          </span>
          {shipped.map(o => (
            <span key={o.order_number} style={{
              color: 'var(--muted)', fontSize: 'clamp(12px, 1vw, 22px)',
              background: 'var(--card)', border: '1px solid var(--border)',
              borderRadius: 6, padding: '3px 10px',
            }}>
              {o.order_number}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
